import { type LoadingManager, BoxGeometry, BufferGeometry, CapsuleGeometry, Color, DoubleSide, ExtrudeGeometry, Group, type Material, Mesh, MeshBasicMaterial, MeshStandardMaterial, PlaneGeometry, Shape, ShapeGeometry, Vector2 } from 'three';
import type { FrameSilhouette, FrameSpec, TempleConstruction } from '../config/frames';
import { acetateEdgePattern, textureAcetateWall } from './acetateEdgePattern';
import { applyCrystalSurface } from './crystalSurface';
import { FrameObject } from './FrameObject';
import { hybridRegions, finishHybridShoulderNormals } from './hybridRegions';
import { createHybridNosePads } from './hybridNosePads';
import { refineCapTriangles } from './refineCapTriangles';
import { subdivideForWrap } from './subdivideForWrap';
import { createAcetateMaterial, createLensMaterial, createPhotoAcetateMaterial, createRivetMaterial, PhotoTextures } from './materials';

/**
 * Builds a placeholder frame when no GLB exists yet.
 *
 * Two levels of fidelity:
 *  - with `spec.silhouette` (front and temple contours extracted from the
 *    product photos by tools/silhouette), the front reproduces the real model's
 *    shape at true scale, with a mild wrap (base curve) and photo-derived temples;
 *  - otherwise a generic rounded-rectangle frame from the catalogue dimensions.
 *
 * Both are geometrically honest (true width, lens size, bridge, temple length)
 * so tracking and scale can be validated before the models are digitised.
 * Coordinate convention: see config/frames.ts.
 */
/**
 * A203 (David, 11 octobre : « je vois pas les branches dans les verres sur beaucoup de lunettes ») : no depth mask
 * behind the lenses any more. The mask (`occludeRearTemples`, 157 models) hid everything seen through a lens,
 * so the far temple vanished as soon as the head turned, where a real clear or tinted lens shows it. The head
 * occluder alone now decides: the temple is hidden where the face, the head or the ear is in front of it, and
 * seen through the lens everywhere else. Decorative openings and `rearOcclusionRegions` keep their masks.
 * `lensRearMask = true` restores the previous behaviour (lab A/B only).
 */
export const PROCEDURAL_OPTIONS = { lensRearMask: false };

export function buildProceduralFrame(spec: FrameSpec, manager?: LoadingManager): FrameObject {
  return spec.silhouette ? buildFromSilhouette(spec, spec.silhouette, manager) : buildGeneric(spec);
}

/** Radius of the front's wrap (base curve ≈ 4): ends sit ~11 mm behind the bridge. */
const WRAP_RADIUS_MM = 250;

function wrapZ(x: number): number {
  return -(x * x) / (2 * WRAP_RADIUS_MM);
}

/**
 * Bends a flat extruded geometry around the vertical axis (front wrap).
 *
 * The normals are transformed analytically with the inverse-transpose
 * Jacobian of the bend (z' = z - x²/2R → n' = (nx + nz·x/R, ny, nz)) instead
 * of being re-averaged: averaged normals over the long, thin triangles of the
 * front would interpolate into visible diagonal streaks under a glossy
 * material, while the exact normal keeps the flat faces perfectly smooth and
 * the walls and bevels crisp.
 */
function applyWrap(geo: BufferGeometry): BufferGeometry {
  subdivideForWrap(geo);
  const pos = geo.getAttribute('position');
  const nor = geo.getAttribute('normal');
  for (let i = 0; i < pos.count; i++) {
    const x = pos.getX(i);
    pos.setZ(i, pos.getZ(i) + wrapZ(x));
    const nx = nor.getX(i) + (nor.getZ(i) * x) / WRAP_RADIUS_MM;
    const ny = nor.getY(i);
    const nz = nor.getZ(i);
    const len = Math.hypot(nx, ny, nz) || 1;
    nor.setXYZ(i, nx / len, ny / len, nz / len);
  }
  finishHybridShoulderNormals(geo);
  pos.needsUpdate = true;
  nor.needsUpdate = true;
  return geo;
}

function polygonShape(points: Array<[number, number]>, smooth = 2): Shape {
  const pts = chaikinClosed(points, smooth);
  const s = new Shape();
  pts.forEach(([x, y], i) => (i === 0 ? s.moveTo(x, y) : s.lineTo(x, y)));
  s.closePath();
  return s;
}

/** Colour only the extruded walls of photographed white lens inlays.
 * The outer acetate wall and decorative openings retain the frame material. */
function markInnerLensWalls(geo: BufferGeometry, lenses: FrameSilhouette['lenses'], smoothing = 2): void {
  const outlines = lenses.map(points => chaikinClosed(points, smoothing));
  const pos = geo.getAttribute('position');
  const index = geo.getIndex();
  const nearLens = (x: number, y: number): boolean => outlines.some(outline => {
    for (let i = 0; i < outline.length; i++) {
      const [ax, ay] = outline[i]!;
      const [bx, by] = outline[(i + 1) % outline.length]!;
      const dx = bx - ax, dy = by - ay;
      const t = Math.max(0, Math.min(1, ((x - ax) * dx + (y - ay) * dy) / (dx * dx + dy * dy || 1)));
      if (Math.hypot(x - (ax + t * dx), y - (ay + t * dy)) < 0.7) return true;
    }
    return false;
  });
  const groups = geo.groups.map(group => ({ ...group }));
  geo.clearGroups();
  for (const group of groups) {
    if (group.materialIndex !== 1) {
      geo.addGroup(group.start, group.count, group.materialIndex);
      continue;
    }
    let runStart = group.start;
    let runMaterial = -1;
    const end = group.start + group.count;
    for (let tri = group.start; tri < end; tri += 3) {
      const ids = [tri, tri + 1, tri + 2].map(i => index ? index.getX(i) : i);
      const x = ids.reduce((sum, id) => sum + pos.getX(id), 0) / 3;
      const y = ids.reduce((sum, id) => sum + pos.getY(id), 0) / 3;
      const material = nearLens(x, y) ? 2 : 1;
      if (material !== runMaterial) {
        if (runMaterial !== -1) geo.addGroup(runStart, tri - runStart, runMaterial);
        runStart = tri;
        runMaterial = material;
      }
    }
    if (runMaterial !== -1) geo.addGroup(runStart, end - runStart, runMaterial);
  }
}

/**
 * Chaikin corner cutting on a closed polygon: `iterations` rounds of
 * subdivision turn the extractor's polyline (1 px tolerance, visible as
 * facets when the frame fills the screen) into a smooth curve; sharp acetate
 * corners are rounded by a fraction of a millimetre, as on the real frame.
 */
function chaikinClosed(points: Array<[number, number]>, iterations: number): Array<[number, number]> {
  let pts = points;
  for (let it = 0; it < iterations; it++) {
    const out: Array<[number, number]> = [];
    const n = pts.length;
    for (let i = 0; i < n; i++) {
      const [x0, y0] = pts[i]!;
      const [x1, y1] = pts[(i + 1) % n]!;
      out.push([0.75 * x0 + 0.25 * x1, 0.75 * y0 + 0.25 * y1], [0.25 * x0 + 0.75 * x1, 0.25 * y0 + 0.75 * y1]);
    }
    pts = out;
  }
  return pts;
}

/** Chaikin on an open polyline (end points kept). */
function chaikinOpen(points: Array<[number, number]>, iterations: number): Array<[number, number]> {
  let pts = points;
  for (let it = 0; it < iterations; it++) {
    const out: Array<[number, number]> = [pts[0]!];
    for (let i = 0; i < pts.length - 1; i++) {
      const [x0, y0] = pts[i]!;
      const [x1, y1] = pts[i + 1]!;
      out.push([0.75 * x0 + 0.25 * x1, 0.75 * y0 + 0.25 * y1], [0.25 * x0 + 0.75 * x1, 0.25 * y0 + 0.75 * y1]);
    }
    out.push(pts[pts.length - 1]!);
    pts = out;
  }
  return pts;
}

/** Scales a polygon about its centroid (used to bury the lens edge inside the rim). */
function scaledPolygon(points: Array<[number, number]>, factor: number): Array<[number, number]> {
  const c = points.reduce((acc, [x, y]) => acc.add(new Vector2(x, y)), new Vector2()).divideScalar(points.length);
  return points.map(([x, y]) => [c.x + (x - c.x) * factor, c.y + (y - c.y) * factor]);
}

/** Rounded edge of the front (mm): a quarter-ellipse `thickness` deep and `size` wide, on both faces. */
const FRONT_EDGE = { thickness: 1.4, size: 0.25, segments: 5 };
/** Rounded edge of the temples (mm): the cross-section becomes a rounded rectangle. */
const TEMPLE_EDGE = { thickness: 0.9, size: 0.2, segments: 5 };
/** End-piece block behind the front: depth to the hinge (catalogue `endPieceDepthMm`, else this), width along the front, edge rounding. */
const DEFAULT_END_PIECE_DEPTH_MM = 6;
const END_PIECE_EDGE = { thickness: 0.8, size: 0.7, segments: 4 };
/** The temple's top edge sits this far under the end piece's top edge. */
const TEMPLE_TOP_INSET_MM = 1.2;
/** Temple width side to side (mm) at the hinge, and its ratio at the tip: acetate temples taper. */
const TEMPLE_WIDTH_MM = 3.6;
const TEMPLE_TIP_WIDTH_RATIO = 0.75;
/** Broad sanity bounds only: retain the measured side-photo height rather than cropping it to a generic thin shank. */
const TEMPLE_HEIGHT_MIN_MM = 3.6;
const TEMPLE_HEIGHT_MAX_MM = 12;
const TEMPLE_HEIGHT_SCALE = 1;

/**
 * Extrudes a shape to `totalDepth` with rounded edges on both faces. The
 * outline at mid-depth is the shape itself (the silhouette stays the one
 * extracted from the photo), the faces are inset by `size`. An acetate front
 * is a polished slab with soft edges: a flat extrusion with a hairline bevel
 * renders as a paper cut-out, the rounded edge catches the room's light along
 * the whole outline and gives the thickness away from any angle.
 */
export function roundedExtrude(shape: Shape, totalDepth: number, edge: { thickness: number; size: number; segments: number }, curveSegments = 4): ExtrudeGeometry {
  const depth = Math.max(0.2, totalDepth - 2 * edge.thickness);
  const geo = new ExtrudeGeometry(shape, {
    depth,
    bevelEnabled: true,
    bevelThickness: edge.thickness,
    bevelSize: edge.size,
    bevelOffset: -edge.size,
    bevelSegments: edge.segments,
    curveSegments,
  });
  roundEdgeNormals(geo, depth, edge.thickness, edge.size);
  return geo;
}

/**
 * ExtrudeGeometry shades its bevel as flat bands (one normal per triangle):
 * under a clearcoat, five bands read as five stripes. The rounded edge is a
 * quarter-ellipse in the profile plane, so its exact normal at height z is
 * known: (thickness·sin, size·cos) in the (in-plane, z) frame, with
 * cos = (z − depth) / thickness. Each vertex gets that normal (the in-plane
 * direction is kept from the flat one), which turns the bands into one
 * continuous curve. Walls and caps are left alone: their flat normal is exact.
 */
export function roundEdgeNormals(geo: BufferGeometry, depth: number, thickness: number, size: number): void {
  const pos = geo.getAttribute('position');
  const nor = geo.getAttribute('normal');
  // Average contour directions at coincident vertices, independently of caps.
  // Averaging complete normals would pull the broad cap into thin bevel faces.
  const directions = new Map<string, [number, number]>();
  const key = (i: number) => [pos.getX(i), pos.getY(i), pos.getZ(i)].map(v => Math.round(v * 1e5)).join(',');
  for (let i = 0; i < pos.count; i++) {
    const x = nor.getX(i), y = nor.getY(i), length = Math.hypot(x, y);
    if (length < 1e-6) continue;
    const k = key(i), sum = directions.get(k) ?? [0, 0];
    sum[0] += x / length; sum[1] += y / length;
    directions.set(k, sum);
  }
  for (let i = 0; i < pos.count; i++) {
    const z = pos.getZ(i);
    let u: number; // cos(t·π/2): 1 on the cap ring, 0 at the wall
    let sign: number;
    // The wall rings (z = 0 and z = depth) belong to the last band of the edge
    // too: they take the in-plane normal (u = 0) so the edge meets the wall
    // without a crease.
    if (z > depth - 1e-4) {
      u = (z - depth) / thickness;
      sign = 1;
    } else if (z < 1e-4) {
      u = -z / thickness;
      sign = -1;
    } else {
      continue;
    }
    u = Math.min(1, Math.max(0, u));
    if (Math.hypot(nor.getX(i), nor.getY(i)) < 1e-6) continue; // cap triangle
    const [nx, ny] = directions.get(key(i))!;
    const inPlane = Math.hypot(nx, ny) || 1;
    const a = thickness * Math.sqrt(1 - u * u);
    const b = size * u;
    const len = Math.hypot(a, b) || 1;
    nor.setXYZ(i, (nx / inPlane) * (a / len), (ny / inPlane) * (a / len), (sign * b) / len);
  }
  nor.needsUpdate = true;
}

/**
 * Moves the rounded edges from the solid edge material (group 1) to the photo
 * (group 0); only the straight walls, seen edge-on, stay solid. The colour of
 * the front then runs continuously over the rounded edge instead of stopping
 * at a darker outline, and the edge's gradient comes from the lighting alone.
 */
export function textureRoundedEdges(geo: BufferGeometry, minNz = 0.2): void {
  const nor = geo.getAttribute('normal');
  const photo: number[] = [];
  const wall: number[] = [];
  for (const g of geo.groups) {
    for (let i = g.start; i < g.start + g.count; i += 3) {
      const nz = (Math.abs(nor.getZ(i)) + Math.abs(nor.getZ(i + 1)) + Math.abs(nor.getZ(i + 2))) / 3;
      (g.materialIndex === 0 || nz > minNz ? photo : wall).push(i, i + 1, i + 2);
    }
  }
  geo.setIndex([...photo, ...wall]);
  geo.clearGroups();
  geo.addGroup(0, photo.length, 0);
  geo.addGroup(photo.length, wall.length, 1);
}

function buildFromSilhouette(spec: FrameSpec, sil: FrameSilhouette, manager?: LoadingManager): FrameObject {
  const { dims, look } = spec;
  // A110Y: each photo file is loaded once for the whole frame (see PhotoTextures).
  const photos = new PhotoTextures(manager);
  const acetate = createAcetateMaterial(look);
  // Caps retain the product photo. Patterned acetate also carries that same
  // albedo through the walls, using inward-safe UVs; plain and crystal fronts
  // retain their independently calibrated edge material.
  const pattern = acetateEdgePattern(spec);
  const edge = createAcetateMaterial({ ...look, acetateColor: darken(look.acetateColor, 0.95) });
  edge.opacity = Math.min(1, look.acetateOpacity + 0.04);
  const templeLook = { ...look, acetateCrystal: false, acetateColor: look.templeAcetateColor ?? look.acetateColor, acetateOpacity: look.templeAcetateOpacity ?? look.acetateOpacity };
  const templeEdge = createAcetateMaterial(templeLook);
  templeEdge.opacity = Math.min(1, templeLook.acetateOpacity + 0.06);
  const photoOpacity = look.acetatePhotoOpacity ?? look.acetateOpacity;
  const templePhotoOpacity = look.templeAcetatePhotoOpacity ?? look.templeAcetateOpacity ?? photoOpacity;
  const innerRimWall = look.innerRimWallColor
    ? createAcetateMaterial({ ...look, acetateColor: look.innerRimWallColor, acetateOpacity: 1 })
    : null;
  const uniformBelowUv: [number, number] | undefined = sil.frontPhoto && look.frontPhotoUniformBelowMm
    ? look.frontPhotoUniformBelowMm.map(y => 1 - (sil.frontPhoto!.cyPx - y * sil.frontPhoto!.pxPerMm) / sil.frontPhoto!.heightPx) as [number, number]
    : undefined;
  let frontMat: Material | Material[] = sil.frontPhoto
    ? [createPhotoAcetateMaterial(photos.get(sil.frontPhoto.dataUrl), look.acetateRoughness, look.acetateColor, photoOpacity, manager, uniformBelowUv), edge, ...(innerRimWall ? [innerRimWall] : [])]
    : acetate;
  let templeMat: Material | Material[] = sil.templePhoto ? [createPhotoAcetateMaterial(photos.get(sil.templePhoto.dataUrl), look.acetateRoughness, templeLook.acetateColor, templePhotoOpacity, manager), templeEdge] : acetate;
  if (pattern.front && Array.isArray(frontMat)) textureAcetateWall(edge, frontMat[0] as ReturnType<typeof createAcetateMaterial>);
  if (pattern.temple && Array.isArray(templeMat)) textureAcetateWall(templeEdge, templeMat[0] as ReturnType<typeof createAcetateMaterial>);
  if (look.acetateCrystal) {
    applyCrystalSurface(edge, true);
    applyCrystalSurface((Array.isArray(frontMat) ? frontMat[0] : frontMat) as ReturnType<typeof createAcetateMaterial>, true);
  }
  const lensMat = createLensMaterial(look);
  const model = new Group();
  model.name = 'procedural-silhouette';
  const hybrid = spec.hybridMetal;
  const metal = hybrid ? new MeshStandardMaterial({ color: hybrid.color, metalness: 0.85, roughness: hybrid.roughness ?? 0.23 }) : null;
  if (metal) metal.userData.fixedMetal = true;

  const frontContour = (points: Array<[number,number]>) => polygonShape(points,sil.contourSmoothing ?? 2);

  // --- Front: real outer contour with the two lens openings as holes.
  const frontShape = frontContour(sil.outer);
  frontShape.holes.push(frontContour(sil.lenses[0]), frontContour(sil.lenses[1]));
  for (const opening of sil.decorativeCutouts ?? []) frontShape.holes.push(frontContour(opening));
  const rear = spec.rearLaminate;
  if (rear && (!(rear.thicknessMm > 0) || rear.thicknessMm >= dims.frontDepthMm - 0.5)) throw new Error('Invalid laminate thickness');
  const primaryDepth = dims.frontDepthMm - (rear?.thicknessMm ?? 0);
  const primaryEdge = { ...FRONT_EDGE, thickness: Math.min(FRONT_EDGE.thickness, primaryDepth * 0.35) };
  let frontFlat: BufferGeometry = roundedExtrude(frontShape, primaryDepth, primaryEdge);
  if (sil.refineFrontCaps) refineCapTriangles(frontFlat);
  // Preserve the existing frontmost plane and rear boundary of the complete stack.
  const frontmostZ = dims.frontDepthMm - FRONT_EDGE.thickness;
  frontFlat.translate(0, 0, frontmostZ - primaryDepth + primaryEdge.thickness);
  if (sil.frontPhoto) {
    // Photo projection: shape coordinates (mm) → pixel of the crop → UV.
    const ph = sil.frontPhoto;
    const photoOpenings = [...sil.lenses, ...(sil.decorativeCutouts ?? [])];
    applyUv(frontFlat, (x, y) => {
      const inset = look.frontPhotoLensInsetMm ?? 0;
      if (inset > 0) {
        // Move only the sampled pixel away from the photographed glass seam.
        // The traced silhouette and actual lens geometry remain unchanged.
        for (const lens of photoOpenings) {
          let best = Infinity, qx = x, qy = y;
          for (let i=0;i<lens.length;i++) {
            const a=lens[i]!, b=lens[(i+1)%lens.length]!;
            const dx=b[0]-a[0],dy=b[1]-a[1];
            const t=Math.max(0,Math.min(1,((x-a[0])*dx+(y-a[1])*dy)/(dx*dx+dy*dy||1)));
            const px=a[0]+t*dx,py=a[1]+t*dy,d=Math.hypot(x-px,y-py);
            if(d<best){best=d;qx=px;qy=py;}
          }
          if(best<inset){
            const cx=lens.reduce((n,p)=>n+p[0],0)/lens.length,cy=lens.reduce((n,p)=>n+p[1],0)/lens.length;
            const dx=qx-cx,dy=qy-cy,len=Math.hypot(dx,dy)||1;
            x=qx+dx/len*inset;y=qy+dy/len*inset;
          }
        }
      }
      return [(ph.cxPx+x*ph.pxPerMm)/ph.widthPx,1-(ph.cyPx-y*ph.pxPerMm)/ph.heightPx];
    });
    textureRoundedEdges(frontFlat);
  }
  // Classify inner walls before the region split so metal slots are never overwritten.
  if (hybrid && metal) {
    if (innerRimWall && sil.frontPhoto) markInnerLensWalls(frontFlat, sil.lenses, sil.contourSmoothing ?? 2);
    const mats = Array.isArray(frontMat) ? [...frontMat] : [frontMat];
    const old = frontFlat;
    const metalIndex=mats.length; mats.push(metal);
    let photoMetalIndex: number | undefined;
    if(sil.frontPhoto) {
      const photo=(mats[0] as MeshStandardMaterial).map;
      if(photo){const polished=metal.clone();polished.map=photo;polished.color.set('#ffffff');polished.roughness=.17;polished.envMapIntensity=.75;polished.userData.fixedMetal=true;photoMetalIndex=mats.length;mats.push(polished);}
    }
    frontFlat = hybridRegions(old, hybrid.front, metalIndex, photoMetalIndex);
    old.dispose(); frontMat = mats;
  }
  const frontGeo = applyWrap(frontFlat);
  if (!hybrid && innerRimWall && sil.frontPhoto) markInnerLensWalls(frontGeo, sil.lenses, sil.contourSmoothing ?? 2);
  const front = new Mesh(frontGeo, frontMat);
  front.name = 'front';
  front.userData.acetateRole = 'primary';
  front.castShadow = true;
  model.add(front);
  if (hybrid?.nosePads && metal) model.add(createHybridNosePads(hybrid.nosePads, metal, WRAP_RADIUS_MM));

  // Relief rings share the source photo projection and remain open over the lens.
  for (const [index, rim] of (sil.raisedRims ?? []).entries()) {
    if (!(rim.depthMm > 0) || rim.depthMm > dims.frontDepthMm) throw new Error('Invalid raised rim depth');
    const shape = frontContour(rim.outer);
    shape.holes.push(frontContour(sil.lenses[rim.lensIndex]));
    const bevel = { thickness: Math.min(0.35, rim.depthMm * 0.3), size: 0.12, segments: 5 };
    const flat = roundedExtrude(shape, rim.depthMm, bevel);
    // Meet the base without overlapping outer walls at the relief seam.
    flat.translate(0, 0, frontmostZ + bevel.thickness);
    if (sil.frontPhoto) {
      const ph = sil.frontPhoto;
      applyUv(flat, (x, y) => [(ph.cxPx + x * ph.pxPerMm) / ph.widthPx, 1 - (ph.cyPx - y * ph.pxPerMm) / ph.heightPx]);
      textureRoundedEdges(flat);
    }
    const ringEdge = rim.color ? createAcetateMaterial({ ...look, acetateColor: rim.color }) : edge;
    const ringMaterial = rim.color && sil.frontPhoto
      ? [createPhotoAcetateMaterial(photos.get(sil.frontPhoto.dataUrl), look.acetateRoughness, rim.color, photoOpacity, manager), ringEdge]
      : rim.color ? ringEdge : frontMat;
    const ring = new Mesh(applyWrap(flat), ringMaterial);
    ring.name = `front_raised_rim_${index}`;
    ring.userData.acetateRole = 'primary';
    ring.castShadow = true;
    model.add(ring);
  }

  if (sil.stitchPhoto) {
    const photo = sil.stitchPhoto;
    const stitchMap = photos.get(photo.dataUrl);
    const plane = new PlaneGeometry(photo.widthPx / photo.pxPerMm,
      photo.heightPx / photo.pxPerMm, 64, 16);
    plane.translate((photo.widthPx / 2 - photo.cxPx) / photo.pxPerMm,
      (photo.cyPx - photo.heightPx / 2) / photo.pxPerMm, dims.frontDepthMm + 0.28);
    const stitch = new Mesh(applyWrap(plane), new MeshBasicMaterial({
      map: stitchMap, transparent: true, depthWrite: false, side: DoubleSide,
      toneMapped: false,
    }));
    stitch.name = 'front_stitch_decal';
    stitch.userData.acetateRole = 'decal';
    stitch.renderOrder = 3;
    model.add(stitch);
  }

  // The catalogue photo is shot with the temples folded behind the front.
  // In try-on those same 3D temples must remain visible from the side, but
  // must not project through the decorative openings onto the wearer's cheek.
  // A depth-only surface just behind the lens and each opening hides that
  // rear geometry while leaving the camera image visible through the holes.
  // A203: the lenses are no longer masked (PROCEDURAL_OPTIONS): the temple seen through a lens stays visible.
  const maskLenses = PROCEDURAL_OPTIONS.lensRearMask && Boolean(sil.occludeRearTemples);
  if (sil.decorativeCutouts?.length || maskLenses || sil.rearOcclusionRegions?.length) {
    const maskMaterial = new MeshBasicMaterial({ colorWrite: false, side: DoubleSide });
    const maskedLenses = maskLenses ? sil.lenses : [];
    for (const [index, opening] of [...maskedLenses, ...(sil.decorativeCutouts ?? []), ...(sil.rearOcclusionRegions ?? [])].entries()) {
      // Match the visible lens footprint and sit immediately behind its front
      // surface. A mask at the rear of the frame leaves a parallax slit at the
      // outer corners, exposing temple fragments through an otherwise clean lens.
      const isLens = index < maskedLenses.length;
      const lensDepth = Math.max(0.6, Math.max(0.2, dims.frontDepthMm - 2 * FRONT_EDGE.thickness) - 0.3);
      const maskGeometry = new ShapeGeometry(frontContour(isLens ? scaledPolygon(opening, 1.006) : opening), 4);
      const lensFrontZ = sil.lensFrontInsetMm === undefined ? 0.15 + lensDepth : frontmostZ - sil.lensFrontInsetMm;
      maskGeometry.translate(0, 0, isLens ? lensFrontZ - 0.02 : -0.5);
      const mask = new Mesh(applyWrap(maskGeometry), maskMaterial);
      mask.name = `rear_visibility_mask_${index}`;
      mask.userData.acetateRole = 'mask';
      mask.renderOrder = -1;
      model.add(mask);
    }
  }

  if (rear) {
    const shape = frontContour(rear.outer);
    shape.holes.push(frontContour(sil.lenses[0]), frontContour(sil.lenses[1]));
    for (const opening of sil.decorativeCutouts ?? []) shape.holes.push(frontContour(opening));
    const bevel = { thickness: Math.min(0.35, rear.thicknessMm * 0.3), size: 0.12, segments: 5 };
    const flat = roundedExtrude(shape, rear.thicknessMm, bevel);
    flat.translate(0, 0, -FRONT_EDGE.thickness + bevel.thickness);
    const rearLook = { ...look, acetateColor: rear.color, acetateOpacity: rear.opacity, acetateRoughness: rear.roughness };
    const wall = createAcetateMaterial(rearLook);
    let material: Material | Material[] = wall;
    if (sil.frontPhoto) {
      const ph = sil.frontPhoto;
      applyUv(flat, (x, y) => [(ph.cxPx + x * ph.pxPerMm) / ph.widthPx, 1 - (ph.cyPx - y * ph.pxPerMm) / ph.heightPx]);
      textureRoundedEdges(flat);
      material = [createPhotoAcetateMaterial(photos.get(ph.dataUrl), rear.roughness, rear.color, rear.opacity, manager), wall];
    }
    const plate = new Mesh(applyWrap(flat), material);
    plate.name = 'front_laminate_rear'; plate.castShadow = true;
    // Preserve the bonded contrast when changing the primary acetate.
    plate.userData.acetateRole = 'secondary';
    model.add(plate);
  }

  // --- Lenses: thin, seated in the straight part of the opening (the rounded
  // inner edge of the rim overhangs the lens edge, as the groove does), a
  // hair larger than the opening so the edge is buried in the acetate.
  const wallDepth = Math.max(0.2, dims.frontDepthMm - 2 * FRONT_EDGE.thickness);
  sil.lenses.forEach((poly, i) => {
    const lensDepth = Math.max(0.6, wallDepth - 0.3);
    const lensGeo = new ExtrudeGeometry(frontContour(scaledPolygon(poly, sil.lensSeatScale ?? 1.006)), { depth: lensDepth, bevelEnabled: false, curveSegments: 4 });
    // A thin hybrid rim needs its glass seated near the front cap to close
    // the oblique-view parallax gap. Other models retain their existing seat.
    const lensZ = sil.lensFrontInsetMm === undefined ? 0.15 : frontmostZ - sil.lensFrontInsetMm - lensDepth;
    lensGeo.translate(0, 0, lensZ);
    const lens = new Mesh(applyWrap(lensGeo), lensMat);
    lens.name = i === 0 ? 'lens_L' : 'lens_R';
    lens.renderOrder = 2;
    model.add(lens);
  });

  // --- Hinge rivets on the end pieces (the visible pins of a riveted hinge) — the photo already shows them.
  if (look.rivetLengthMm > 0 && !sil.frontPhoto) {
    const rivetMat = createRivetMaterial();
    const r = 1.1;
    const rivetGeo = new CapsuleGeometry(r, Math.max(0.1, look.rivetLengthMm - 2 * r), 4, 12);
    rivetGeo.rotateZ(Math.PI / 2); // capsule axis → X
    const rivetX = dims.widthMm / 2 - 8.5;
    for (const side of [1, -1] as const) {
      const rivet = new Mesh(rivetGeo, rivetMat);
      rivet.name = side > 0 ? 'rivet_L' : 'rivet_R';
      rivet.position.set(side * rivetX, sil.hingeY, wrapZ(side * rivetX) + dims.frontDepthMm + 0.35);
      rivet.rotation.y = wrapAngle(side * rivetX); // follow the wrap so the pin lies on the surface
      model.add(rivet);
    }
  }

  if (look.butterflyRivets?.length) {
    const shape = new Shape();
    shape.moveTo(-1.6,0);
    shape.bezierCurveTo(-1.8,1.05,-0.9,0.9,0,0.4);
    shape.bezierCurveTo(0.9,0.9,1.8,1.05,1.6,0);
    shape.bezierCurveTo(1.8,-1.05,0.9,-0.9,0,-0.4);
    shape.bezierCurveTo(-0.9,-0.9,-1.8,-1.05,-1.6,0);
    const metal = createRivetMaterial();
    metal.color.set('#9da3a4'); metal.metalness=0.65; metal.roughness=0.3;
    for (const [x,y] of look.butterflyRivets) {
      const geo = new ExtrudeGeometry(shape,{depth:0.1,bevelEnabled:false,curveSegments:12});
      geo.scale(1.42,1.25,1);
      geo.translate(x,y,dims.frontDepthMm+0.06);
      const insert = new Mesh(applyWrap(geo),metal);
      insert.name=x>0?'butterfly_rivet_L':'butterfly_rivet_R';
      model.add(insert);
    }
  }

  // --- End pieces (tenons): on an acetate frame the hinge does not sit on the
  // back of the front but at the rear of a block that runs `endPieceDepthMm`
  // backwards from it, with the end piece's own height. The temple butts
  // against that block, flush with its outer face and just under its top
  // edge — the junction the photos show, at the hinge, on every model
  // (heights and depth come from the silhouette and the catalogue entry).
  const construction: TempleConstruction = spec.temple ?? {
    widthMm: TEMPLE_WIDTH_MM, tipWidthRatio: TEMPLE_TIP_WIDTH_RATIO,
    topInsetMm: TEMPLE_TOP_INSET_MM, hingeInsetMm: 1.5, outerInsetMm: 0.15,
    profileStartMm: 12, profileMode: 'reconstruct-occluded-bottom',
    profileFitRangeMm: [12, dims.templeMm * 0.6],
  }; // Compatibility fallback only; newly digitised models supply their own construction.
  if (!(construction.widthMm > 0) || !(construction.tipWidthRatio > 0)) throw new Error('Invalid temple construction');
  const hingeOffsetY = construction.hingeOffsetYMm ?? [0, 0];
  const templeWidth = construction.widthMm;
  const templeEdgeGeometry = { ...TEMPLE_EDGE, thickness: Math.min(TEMPLE_EDGE.thickness, templeWidth * 0.4) };
  const endPieceEdge = { ...END_PIECE_EDGE, size: Math.min(END_PIECE_EDGE.size, templeWidth * 0.2) };
  const endDepth = dims.endPieceDepthMm ?? DEFAULT_END_PIECE_DEPTH_MM;
  const outerX = Math.max(...sil.outer.map(([x]) => x));
  const end = endPieceExtent(sil.outer, outerX);
  const hingeX = dims.widthMm / 2 - construction.hingeInsetMm;
  // Vertical placement of the temples: top edge TEMPLE_TOP_INSET_MM under the
  // end piece's top edge (relative to the hinge axis); the side photo's own y
  // reference is not the hinge axis, so its profile is shifted accordingly.
  const templeTopY = (construction.topAtHingeMm ?? (end.top - construction.topInsetMm)) - sil.hingeY;
  let templeShape = sil.templeTop && sil.templeBottom ? templeFromProfile(sil.templeTop, sil.templeBottom, dims.templeMm, templeTopY, construction) : templeProfile(dims.templeMm);
  let profileHeightScale = 1;
  if (construction.profileHeightMm !== undefined) {
    const points = templeShape.getPoints();
    const start = points.filter(p => Math.abs(p.x) < 1e-5).map(p => p.y);
    const height = Math.max(...start)-Math.min(...start);
    if (!(construction.profileHeightMm > 0) || !(height > 0)) throw new Error('Invalid profile height');
    profileHeightScale = construction.profileHeightMm / height;
    templeShape = polygonShape(points.map(p => [p.x,templeTopY+(p.y-templeTopY)*profileHeightScale]),0);
  }
  const hingeProfileYs = templeShape.getPoints().filter(p => Math.abs(p.x) < 1e-5).map(p => p.y + sil.hingeY);
  const junctionTop = Math.max(...hingeProfileYs);
  const junctionBottom = Math.min(...hingeProfileYs);
  const photoTopRaw = sil.templeTop?.find(([sm]) => sm >= construction.profileStartMm)?.[1] ?? templeTopY;
  const photoYShift = photoTopRaw - templeTopY; // shape y (hinge-relative) + shift = photo y
  const templeOffsetX = outerX - construction.outerInsetMm - hingeX - (templeWidth - templeEdgeGeometry.thickness);
  // One cross-section defines the shank and the connector, in the hinge frame.
  const junctionCentreX = hingeX + templeOffsetX + templeWidth / 2 - templeEdgeGeometry.thickness;
  const hingeZ = wrapZ(hingeX) - endDepth - FRONT_EDGE.thickness + 0.5;
  const templePhotoMat = sil.templePhoto ? createPhotoAcetateMaterial(photos.get(sil.templePhoto.dataUrl), look.acetateRoughness, templeLook.acetateColor, templePhotoOpacity, manager) : null;
  for (const side of [1, -1] as const) {
    const cx = side * junctionCentreX;
    const metalHinge = hybrid?.temple.find(r => r.fromMm <= 0 && r.toMm > 0);
    const connectorWidth = metalHinge?.depthMm ?? templeWidth;
    const blockShape = roundedRect(cx, (junctionTop + junctionBottom) / 2, connectorWidth, junctionTop - junctionBottom, hybrid ? Math.min(1, connectorWidth * 0.25) : 1.0);
    // Flat coordinates: spans z ∈ [−(endDepth + FRONT_EDGE.thickness), −0.4] (1 mm into the front's slab), then wrapped like the front.
    // Extend the connector behind the hinge plane across its entire width.
    // A wide shank must not detach at the inner edge of the curved front.
    const nearestX = Math.max(0, Math.abs(cx) - templeWidth / 2);
    const rearFlatZ = Math.min(-endDepth - FRONT_EDGE.thickness, hingeZ - 0.5 - wrapZ(nearestX));
    // A thin metal tenon is centred inside the acetate slab. Meet its rear
    // surface instead of stopping at the legacy acetate connector plane.
    const frontMetalAtHinge = hybrid?.front.find(r =>
      cx > r.fromMm && cx < r.toMm &&
      (r.yMinMm === undefined || (junctionTop+junctionBottom)/2 > r.yMinMm) &&
      (r.yMaxMm === undefined || (junctionTop+junctionBottom)/2 < r.yMaxMm));
    const connectorFrontZ = frontMetalAtHinge
      ? frontmostZ-primaryDepth/2-frontMetalAtHinge.depthMm/2+.25 : -.4;
    const total = connectorFrontZ - rearFlatZ;
    const blockGeo = roundedExtrude(blockShape, total, hybrid ? { ...endPieceEdge, size: Math.min(.15, connectorWidth * .1) } : endPieceEdge, 12);
    blockGeo.translate(0, 0, connectorFrontZ - (total - endPieceEdge.thickness));
    let blockMat: Material | Material[] = look.templeAcetateColor && !construction.connectorUsesFrontAcetate ? templeEdge : edge;
    if (pattern.front && sil.frontPhoto && Array.isArray(frontMat)) {
      // The first side-photo pixels often include the white studio gap behind
      // the hinge. Continue the front end-piece's own acetate through this
      // block instead; every face uses a point safely inside the solid tenon.
      const ph = sil.frontPhoto;
      const sampleX = side*(outerX-Math.max(1.2,connectorWidth*.5));
      const midY = (end.top+end.bottom)/2;
      const low = Math.min(end.bottom+.9,midY), high = Math.max(end.top-.9,midY);
      applyUv(blockGeo, (_x,y) => [(ph.cxPx+sampleX*ph.pxPerMm)/ph.widthPx,
        1-(ph.cyPx-Math.max(low,Math.min(high,y))*ph.pxPerMm)/ph.heightPx]);
      blockMat = [frontMat[0]!, edge];
    } else if (sil.templePhoto && templePhotoMat && (!construction.solidConnector || pattern.temple)) {
      // Outer face: the end piece as the side photo shows it (hinge, rivet);
      // every other face solid acetate.
      const tp = sil.templePhoto;
      applySideUv(blockGeo, side, (z, y) => {
        const distance = pattern.temple ? Math.max(construction.profileStartMm, 12, -z+.6) : -z+.6;
        let photoY = y - sil.hingeY + photoYShift;
        if (pattern.temple && sil.templeTop && sil.templeBottom) {
          const top = sampleProfile(sil.templeTop, distance), bottom = sampleProfile(sil.templeBottom, distance);
          const inset = Math.min(0.35, (top-bottom)*0.2);
          photoY = Math.max(bottom+inset, Math.min(top-inset, photoY));
        }
        return [(tp.hingeXPx + tp.direction * distance * tp.pxPerMm) / tp.widthPx, 1 - (tp.y0Px-photoY*tp.pxPerMm)/tp.heightPx];
      });
      blockMat = [templePhotoMat, look.templeAcetateColor ? templeEdge : edge];
    }
    if (hybrid && metal) blockMat = metal;
    blockGeo.translate(0, hingeOffsetY[side > 0 ? 0 : 1]!, 0);
    const block = new Mesh(applyWrap(blockGeo), blockMat);
    block.name = side > 0 ? 'endpiece_L' : 'endpiece_R';
  block.userData.acetateRole = 'primary';
    block.castShadow = true;
    model.add(block);
  }

  const sideOpenings: Array<Array<[number, number]>> = [];
  if (sil.templeLensOpenings?.length) {
    if (!sil.templeTop || !sil.templeBottom || construction.profileMode !== 'measured' || construction.profileStartMm !== 0) throw new Error('Side lenses require measured full temple');
    const profileLength = sil.templeTop.at(-1)![0];
    if (!(profileLength > 0)) throw new Error('Invalid side lens profile length');
    for (const raw of sil.templeLensOpenings) {
      if (raw.length < 8 || raw.some(([s,y]) => !Number.isFinite(s) || !Number.isFinite(y) || s <= 0 || s >= profileLength || y >= sampleProfile(sil.templeTop!, s) - 0.4 || y <= sampleProfile(sil.templeBottom!, s) + 0.4)) throw new Error('Side lens outside temple');
      const opening = raw.map(([s,y]) => [s*dims.templeMm/profileLength,y-photoYShift] as [number,number]);
      sideOpenings.push(opening);
      templeShape.holes.push(polygonShape(opening));
    }
  }

  // --- Temples: use the same hinge profile as the end piece above.
  let templeGeo: BufferGeometry = roundedExtrude(templeShape, templeWidth, templeEdgeGeometry);
  taperTemple(templeGeo, dims.templeMm, templeEdgeGeometry.thickness, construction.tipWidthRatio);
  if (sil.templePhoto) {
    // The side photo's abscissa starts at the front of the end piece: the
    // temple's own s starts `endDepth` further back, and its first centimetre
    // (hinge, lens edge) samples the shank instead.
    const tp = sil.templePhoto;
    const profileLength = sil.templeTop?.at(-1)?.[0] ?? dims.templeMm;
    applyUv(templeGeo, (sMm, y) => {
      const s = Math.max(construction.profileStartMm, sMm * profileLength / dims.templeMm);
      let photoY = templeTopY + (y-templeTopY)/profileHeightScale + photoYShift;
      if (construction.profileMode === 'measured' && sil.templeTop && sil.templeBottom) {
        // A rounded bevel samples beyond its polygon edge. Keep its photo UV
        // inside the photographed shank so the white sweep between two temples
        // cannot become a bright patch on a dark/translucent branch.
        const top = sampleProfile(sil.templeTop, s);
        const bottom = sampleProfile(sil.templeBottom, s);
        if (construction.junctionHeightMm !== undefined) {
          const first = Math.max(0,sil.templeTop.findIndex(([v]) => v >= construction.profileStartMm));
          const delta = sil.templeTop[first]![1]-sil.templeBottom[first]![1]-construction.junctionHeightMm;
          const lift = delta*Math.max(0,1-sMm/12);
          photoY = top-(top-photoY)*(top-bottom)/Math.max(0.1,top-bottom-lift);
        }
        const inset = Math.min(0.75, (top - bottom) * 0.2);
        photoY = Math.max(bottom + inset, Math.min(top - inset, photoY));
      }
      return [(tp.hingeXPx + tp.direction * s * tp.pxPerMm) / tp.widthPx,
        1 - (tp.y0Px - photoY * tp.pxPerMm) / tp.heightPx];
    });
    textureRoundedEdges(templeGeo);
  }
  if (hybrid && metal) {
    const mats = Array.isArray(templeMat) ? [...templeMat] : [templeMat];
    const old = templeGeo;
    const metalIndex=mats.length; mats.push(metal);
    let photoMetalIndex: number | undefined;
    if(sil.templePhoto){const photo=(mats[0] as MeshStandardMaterial).map;if(photo){const polished=metal.clone();polished.map=photo;polished.color.set('#ffffff');polished.roughness=.17;polished.envMapIntensity=.75;polished.userData.fixedMetal=true;photoMetalIndex=mats.length;mats.push(polished);}}
    templeGeo = hybridRegions(old, hybrid.temple, metalIndex, photoMetalIndex);
    old.dispose(); templeMat = mats;
  }
  // The temple's outer face is flush with the end piece (0.15 mm inside its
  // outline): from the side the acetate runs continuously from block to temple,
  // from the front the temple stays behind the end piece.

  // A201: what the 3D engraving needs to sit on the temple's faces (TempleEngraving): the outline (shape x from
  // the hinge backwards, y up), the length, the width side to side, the edge and the taper.
  const engraveGeom = {
    lengthMm: dims.templeMm, widthMm: templeWidth, edgeMm: templeEdgeGeometry.thickness, tipRatio: construction.tipWidthRatio,
    outline: templeShape.getPoints().map(p => [p.x, p.y] as [number, number]),
  };

  const hingeL = new Group();
  hingeL.name = 'hinge_L';
  hingeL.position.set(hingeX, sil.hingeY + hingeOffsetY[0]!, hingeZ);
  const templeL = new Mesh(templeGeo, templeMat);
  templeL.name = 'temple_L';
  templeL.userData.acetateRole = 'primary';
  templeL.userData.engraveGeom = engraveGeom;
  templeL.castShadow = true;
  templeL.rotation.y = Math.PI / 2; // shape +x → world -Z (backwards), extrusion → +X
  templeL.position.x = templeOffsetX;
  hingeL.add(templeL);
  model.add(hingeL);

  const hingeR = new Group();
  hingeR.name = 'hinge_R';
  hingeR.position.set(-hingeX, sil.hingeY + hingeOffsetY[1]!, hingeZ);
  hingeR.scale.x = -1; // mirror of the left temple
  const templeR = new Mesh(templeGeo, templeMat);
  templeR.name = 'temple_R';
  templeR.userData.acetateRole = 'primary';
  templeR.userData.engraveGeom = engraveGeom;
  templeR.castShadow = true;
  templeR.rotation.y = Math.PI / 2;
  templeR.position.x = templeOffsetX;
  hingeR.add(templeR);
  model.add(hingeR);

  for (const [i, opening] of sideOpenings.entries()) {
    const sideLensGeo = new ExtrudeGeometry(polygonShape(scaledPolygon(opening, 1.006)), {
      depth: Math.max(0.3, templeWidth - 2 * templeEdgeGeometry.thickness - 0.2), bevelEnabled: false, curveSegments: 4,
    });
    sideLensGeo.translate(0, 0, 0.1);
    taperTemple(sideLensGeo, dims.templeMm, templeEdgeGeometry.thickness, construction.tipWidthRatio);
    for (const [suffix, hinge] of [['L', hingeL], ['R', hingeR]] as const) {
      const sideLens = new Mesh(sideLensGeo, createLensMaterial(look));
      sideLens.name = `lens_temple_${suffix}_${i}`;
      sideLens.userData.templeLens = true;
      sideLens.rotation.y = Math.PI / 2;
      sideLens.position.x = templeOffsetX;
      sideLens.renderOrder = 2;
      hinge.add(sideLens);
    }
  }

  return new FrameObject(spec, model, hingeL, hingeR);
}

function sampleProfile(points: Array<[number, number]>, distance: number): number {
  if (distance <= points[0]![0]) return points[0]![1];
  for (let i = 1; i < points.length; i++) {
    const [s, y] = points[i]!;
    if (distance <= s) {
      const [lastS, lastY] = points[i - 1]!;
      return lastY + (y - lastY) * (distance - lastS) / (s - lastS);
    }
  }
  return points.at(-1)![1];
}

/**
 * UVs and material groups of an end-piece block: the outer face (normal
 * towards `side`) and its rounded edges take the side photo through `map(z, y)`
 * (flat coordinates, before the wrap), every other face goes to material 1.
 */
function applySideUv(geo: BufferGeometry, side: 1 | -1, map: (z: number, y: number) => [number, number]): void {
  const pos = geo.getAttribute('position');
  const nor = geo.getAttribute('normal');
  const uv = geo.getAttribute('uv');
  for (let i = 0; i < pos.count; i++) {
    const [u, v] = map(pos.getZ(i), pos.getY(i));
    uv.setXY(i, u, v);
  }
  uv.needsUpdate = true;
  const photo: number[] = [];
  const solid: number[] = [];
  for (const g of geo.groups) {
    for (let i = g.start; i < g.start + g.count; i += 3) {
      const nx = (nor.getX(i) + nor.getX(i + 1) + nor.getX(i + 2)) / 3;
      (nx * side > 0.45 ? photo : solid).push(i, i + 1, i + 2);
    }
  }
  geo.setIndex([...photo, ...solid]);
  geo.clearGroups();
  geo.addGroup(0, photo.length, 0);
  geo.addGroup(photo.length, solid.length, 1);
}

/** Vertical extent of the outline at its outer end (the end piece where the hinge sits). */
function endPieceExtent(outer: Array<[number, number]>, outerX: number): { top: number; bottom: number } {
  const ys = outer.filter(([x]) => x >= outerX - 2.5).map(([, y]) => y);
  if (ys.length < 2) return { top: 4, bottom: -4 };
  return { top: Math.max(...ys), bottom: Math.min(...ys) };
}

/**
 * Tapers an extruded temple: full width at the hinge, `tipRatio` of it at the
 * tip, keeping the inner face (towards the head, z = −edgeThickness) in place
 * so the outer face is what draws in. Shape x = distance from the hinge.
 */
function taperTemple(geo: BufferGeometry, lengthMm: number, edgeThickness: number, tipRatio: number): void {
  const pos = geo.getAttribute('position');
  const nor = geo.getAttribute('normal');
  const zMin = -edgeThickness;
  for (let i = 0; i < pos.count; i++) {
    const t = Math.min(1, Math.max(0, pos.getX(i) / lengthMm));
    const k = 1 + (tipRatio - 1) * t;
    const z = pos.getZ(i) - zMin;
    const derivative = t > 0 && t < 1 ? (tipRatio - 1) / lengthMm : 0;
    const nx = nor.getX(i) - derivative * z * nor.getZ(i) / k;
    const ny = nor.getY(i), nz = nor.getZ(i) / k;
    const length = Math.hypot(nx, ny, nz) || 1;
    nor.setXYZ(i, nx / length, ny / length, nz / length);
    pos.setZ(i, zMin + z * k);
  }
  pos.needsUpdate = true;
}

/** Multiplies an sRGB hex colour by `factor` (< 1 darkens, > 1 lightens). */
function darken(hex: string, factor: number): string {
  const c = new Color(hex);
  c.multiplyScalar(factor);
  return '#' + c.getHexString();
}

/**
 * Rewrites the UVs of an extruded geometry from its shape coordinates. Walls
 * and bevels sample the photo `insetMm` inside the outline (along their
 * in-plane normal) so the anti-aliased edge and the background never bleed
 * onto the edges of the frame. Call before any bend (normals are the flat ones).
 */
function applyUv(geo: BufferGeometry, map: (x: number, y: number) => [number, number], insetMm = 1.2): void {
  const pos = geo.getAttribute('position');
  const nor = geo.getAttribute('normal');
  const uv = geo.getAttribute('uv');
  for (let i = 0; i < pos.count; i++) {
    const nz = Math.abs(nor.getZ(i));
    const side = 1 - nz; // 1 on the walls, 0 on the faces, in between on the bevels
    const [u, v] = map(pos.getX(i) - nor.getX(i) * insetMm * side, pos.getY(i) - nor.getY(i) * insetMm * side);
    uv.setXY(i, u, v);
  }
  uv.needsUpdate = true;
}

/** Local rotation of the wrapped surface about Y at abscissa x (its normal tilts by atan(dz/dx)). */
function wrapAngle(x: number): number {
  return Math.atan(x / WRAP_RADIUS_MM);
}

/**
 * Temple side profile from photo samples. The top edge is reliable; the bottom
 * edge is reconstructed from a linear thickness fit on the clean first 60 % of
 * the temple (the far temple overlaps the tip in side photos), with broad sanity bounds only.
 */
function templeFromProfile(topRaw: Array<[number, number]>, bottom: Array<[number, number]>, lengthMm: number, topAtHingeY: number, construction: TempleConstruction): Shape {
  // The first centimetre of the side photo still shows the end piece of the
  // front (taller than the temple): flatten the top edge there to the first
  // clean sample, so the temple never sticks out above the end piece.
  const firstClean = Math.max(0, topRaw.findIndex(([s]) => s >= construction.profileStartMm));
  // Vertical placement: the photo's y reference is not the front's hinge axis;
  // when the caller knows where the top edge must sit (under the end piece's
  // top), the whole profile is shifted so that its top at the hinge is there.
  const shiftY = topAtHingeY !== undefined ? topAtHingeY - topRaw[firstClean]![1] : 0;
  const top = topRaw.map((t, i) => (i < firstClean ? ([t[0], topRaw[firstClean]![1] + shiftY] as [number, number]) : ([t[0], t[1] + shiftY] as [number, number])));
  const n = top.length;
  const fitRange = construction.profileFitRangeMm ?? [topRaw[firstClean]![0], lengthMm * 0.6];
  const clean = topRaw.map((t, i) => [t[0], t[1] - (bottom[i]?.[1] ?? t[1] - 6)] as [number, number]).filter(([s, th]) => s >= fitRange[0]! && s <= fitRange[1]! && th > 3 && th < 12);
  // Least squares thickness(s) = a + b s.
  let a = 6;
  let b = -0.01;
  if (clean.length >= 4) {
    const ms = clean.reduce((acc, [s]) => acc + s, 0) / clean.length;
    const mt = clean.reduce((acc, [, t]) => acc + t, 0) / clean.length;
    const num = clean.reduce((acc, [s, t]) => acc + (s - ms) * (t - mt), 0);
    const den = clean.reduce((acc, [s]) => acc + (s - ms) * (s - ms), 0) || 1;
    b = num / den;
    a = mt - b * ms;
  }
  const thickness = (s: number) => Math.min(TEMPLE_HEIGHT_MAX_MM, Math.max(TEMPLE_HEIGHT_MIN_MM, (a + b * Math.max(s, topRaw[firstClean]![0])) * TEMPLE_HEIGHT_SCALE));
  const scale = lengthMm / Math.max(1e-3, top[n - 1]![0]);

  const topLine = chaikinOpen(top.map(([s, y]) => [s * scale, y] as [number, number]), 2);
  const bottomLine = chaikinOpen(top.map(([s, y], i) => {
    const measuredIndex = Math.max(i, firstClean);
    const measuredBottom = bottom[measuredIndex];
    if (construction.profileMode === 'measured') {
      if (!measuredBottom || measuredBottom[1] >= topRaw[measuredIndex]![1]) throw new Error('Invalid measured temple contour');
      const blend = Math.max(0, 1 - s * scale / 12);
      const delta = construction.junctionHeightMm === undefined ? 0 : topRaw[firstClean]![1] - bottom[firstClean]![1] - construction.junctionHeightMm;
      return [s * scale, measuredBottom[1] + shiftY + delta * blend] as [number, number];
    }
    const blend = Math.max(0, 1 - s * scale / 12);
    const height = construction.junctionHeightMm === undefined ? thickness(s) : thickness(s) + (construction.junctionHeightMm - thickness(0)) * blend;
    return [s * scale, y - height] as [number, number];
  }), 2);
  const shape = new Shape();
  topLine.forEach(([x, y], i) => (i === 0 ? shape.moveTo(x, y) : shape.lineTo(x, y)));
  for (let i = bottomLine.length - 1; i >= 0; i--) shape.lineTo(bottomLine[i]![0], bottomLine[i]![1]);
  shape.closePath();
  return shape;
}

function buildGeneric(spec: FrameSpec): FrameObject {
  const { dims, look } = spec;
  const rim = 3.5; // rim width, mm
  const cornerR = Math.min(7, dims.lensHeightMm * 0.18);
  const halfBridge = dims.bridgeMm / 2;
  const lensCx = halfBridge + dims.lensWidthMm / 2; // lens centre |x|
  const topY = dims.lensHeightMm / 2 + rim;

  const acetate = createAcetateMaterial(look);
  const lensMat = createLensMaterial(look);

  const model = new Group();
  model.name = 'procedural';

  // --- Front: two rims (outer rounded rect with rounded-rect hole).
  for (const side of [-1, 1] as const) {
    const cx = side * lensCx;
    const outer = roundedRect(cx, 0, dims.lensWidthMm + 2 * rim, dims.lensHeightMm + 2 * rim, cornerR + rim);
    const hole = roundedRect(cx, 0, dims.lensWidthMm, dims.lensHeightMm, cornerR);
    outer.holes.push(hole);
    const rimGeo = new ExtrudeGeometry(outer, {
      depth: dims.frontDepthMm,
      bevelEnabled: true,
      bevelThickness: 0.6,
      bevelSize: 0.5,
      bevelSegments: 2,
      curveSegments: 14,
    });
    const rimMesh = new Mesh(rimGeo, acetate);
    rimMesh.name = side > 0 ? 'front_L' : 'front_R';
  rimMesh.userData.acetateRole = 'primary';
    model.add(rimMesh);

    // Lens: slightly inset, thin, sitting inside the rim.
    const lensShape = roundedRect(cx, 0, dims.lensWidthMm - 0.6, dims.lensHeightMm - 0.6, Math.max(1, cornerR - 0.3));
    const lensGeo = new ExtrudeGeometry(lensShape, { depth: 1.2, bevelEnabled: false, curveSegments: 14 });
    const lens = new Mesh(lensGeo, lensMat);
    lens.position.z = dims.frontDepthMm * 0.35;
    lens.name = side > 0 ? 'lens_L' : 'lens_R';
    lens.renderOrder = 2;
    model.add(lens);

    // End piece from the outer rim to the hinge.
    const innerEdge = lensCx + dims.lensWidthMm / 2 + rim;
    const endW = Math.max(2, dims.widthMm / 2 - innerEdge + 1);
    const endGeo = new BoxGeometry(endW, 8, dims.frontDepthMm);
    const end = new Mesh(endGeo, acetate);
    end.position.set(side * (innerEdge - 1 + endW / 2), topY - 4.5, dims.frontDepthMm / 2);
    end.name = side > 0 ? 'endpiece_L' : 'endpiece_R';
  end.userData.acetateRole = 'primary';
    model.add(end);
  }

  // --- Bridge (saddle): joins the two rims near the top.
  const bridgeGeo = new BoxGeometry(dims.bridgeMm + 2 * rim, 7.5, dims.frontDepthMm);
  const bridge = new Mesh(bridgeGeo, acetate);
  bridge.position.set(0, dims.lensHeightMm / 2 - 1.5, dims.frontDepthMm / 2);
  bridge.name = 'bridge';
  bridge.userData.acetateRole = 'primary';
  model.add(bridge);

  // --- Temples on hinge pivots.
  const hingeY = topY - 4.5;
  const templeShape = templeProfile(dims.templeMm);
  const templeGeo = new ExtrudeGeometry(templeShape, { depth: 3.4, bevelEnabled: true, bevelThickness: 0.4, bevelSize: 0.4, bevelSegments: 1, curveSegments: 6 });

  const hingeL = new Group();
  hingeL.name = 'hinge_L';
  hingeL.position.set(dims.widthMm / 2 - 1.2, hingeY, 0.6);
  const templeL = new Mesh(templeGeo, acetate);
  templeL.name = 'temple_L';
  templeL.userData.acetateRole = 'primary';
  templeL.rotation.y = Math.PI / 2; // shape +x → world -Z (backwards), extrusion → +X
  hingeL.add(templeL);
  model.add(hingeL);

  const hingeR = new Group();
  hingeR.name = 'hinge_R';
  hingeR.position.set(-(dims.widthMm / 2 - 1.2), hingeY, 0.6);
  hingeR.scale.x = -1; // mirror of the left temple
  const templeR = new Mesh(templeGeo, acetate);
  templeR.name = 'temple_R';
  templeR.userData.acetateRole = 'primary';
  templeR.rotation.y = Math.PI / 2;
  hingeR.add(templeR);
  model.add(hingeR);

  return new FrameObject(spec, model, hingeL, hingeR);
}

/** Rounded rectangle centred on (cx, cy). */
function roundedRect(cx: number, cy: number, w: number, h: number, r: number): Shape {
  const x = cx - w / 2;
  const y = cy - h / 2;
  const rr = Math.min(r, w / 2, h / 2);
  const s = new Shape();
  s.moveTo(x + rr, y);
  s.lineTo(x + w - rr, y);
  s.quadraticCurveTo(x + w, y, x + w, y + rr);
  s.lineTo(x + w, y + h - rr);
  s.quadraticCurveTo(x + w, y + h, x + w - rr, y + h);
  s.lineTo(x + rr, y + h);
  s.quadraticCurveTo(x, y + h, x, y + h - rr);
  s.lineTo(x, y + rr);
  s.quadraticCurveTo(x, y, x + rr, y);
  return s;
}

/**
 * Generic temple side profile in (length, height) — straight shank, then the
 * ear bend starting at ~76 % of the length. Height tapers from 8 to 5 mm.
 */
function templeProfile(lengthMm: number): Shape {
  const L = lengthMm;
  const bend = L * 0.76;
  const s = new Shape();
  s.moveTo(0, 4);
  s.lineTo(bend * 0.85, 3.6);
  s.quadraticCurveTo(bend + 8, 3.0, bend + 16, -2);
  s.quadraticCurveTo(L - 8, -10, L, -16);
  s.lineTo(L - 1, -19);
  s.quadraticCurveTo(L - 12, -13, bend + 12, -6);
  s.quadraticCurveTo(bend + 2, -2, bend * 0.85, -1.8);
  s.lineTo(0, -4);
  s.closePath();
  return s;
}
